import {
  ContentItem,
  ContentItems,
  ContentWrapper,
  Flex,
  Space,
  Text,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import { isBefore2025 } from '@/features/project/_utils/project';
import { FundSheetModel } from '@/features/project/projects/_models/project';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { KsSheetTypeMetaInformation } from './KsSheetTypeMetaInformation';
import { ProjectInformationHeader } from './ProjectInformationHeader';
import {
  KsCorporateNamePinpointItem,
  ProjectGroupPinpointItem,
  ProjectNumberPinpointItem,
  ProjectOldProjectNumberPinpointItem,
  ProjectPeriodPinpointItem,
  ProjectRemarkPinpointItem,
} from './pinpoint-items/basic-information-pinpoint-fields';

type Props = z.infer<typeof FundSheetModel> & {
  historySavedAt: string | undefined;
};

export const KsInvestmentProjectInformation: FC<Props> = (props) => {
  const {
    id,
    fiscalYear,
    projectNumber,
    oldProjectNumber,
    startYear,
    endYear,
    purpose,
    overview,
    overviewUrl,
    task,
    remark,
    name,
    isNoScheduledEnd,
    isUnknownStart,
    ...rest
  } = FundSheetModel.parse(props);
  const { t } = useTranslation();

  return (
    <>
      <ProjectInformationHeader
        ministryId={rest.ministryId}
        historySavedAt={props.historySavedAt}
        organization={rest.organization}
        relatedOrganizations={rest.relatedOrganizations}
        otherResponsibleOrganizations={rest.otherResponsibleOrganizations}
        peopleInCharge={rest.peopleInCharge}
        businessManagers={rest.businessManagers}
        snapshotId={rest.snapshotId}
        pinpointSheetType="KS_INVESTMENT"
      />

      <ContentWrapper title={t('basic-information')}>
        <ContentItem borderBottom={0}>
          <ContentItems
            items={[
              <ProjectNumberPinpointItem
                key="project-number"
                projectNumber={projectNumber}
                pinpointSheetType="KS_INVESTMENT"
                projectId={id}
              />,
              <ProjectGroupPinpointItem
                key="project-group"
                projectId={id}
                pinpointSheetType="KS_INVESTMENT"
              />,
            ]}
          />
        </ContentItem>

        <Flex vertical gap={4}>
          {rest.fundAdditionalAttributes && (
            <KsSheetTypeMetaInformation
              isBefore2025={isBefore2025({ fiscalYear })}
              fundSheetType={rest.fundAdditionalAttributes.fundSheetType}
              fundSheetNumber={rest.fundAdditionalAttributes.fundSheetNumber}
              fundSheetBranchNumber={
                rest.fundAdditionalAttributes.fundSheetBranchNumber
              }
            />
          )}
          <Text variant="h4" text={name} weight="bold" />
        </Flex>

        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <KsCorporateNamePinpointItem
                key="corporate-name"
                projectId={id}
                pinpointSheetType="KS_INVESTMENT"
                corporateName={rest.fundAdditionalAttributes?.fundOwner}
              />,
              <ProjectPeriodPinpointItem
                key="period"
                projectId={id}
                pinpointSheetType="KS_INVESTMENT"
                startYear={startYear}
                endYear={endYear}
                isUnknownStart={isUnknownStart}
                isNoScheduledEnd={isNoScheduledEnd}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('overview-and-purpose')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <Space key="project-overview" direction="vertical">
                <Text variant="h4" text={t('project-overview')} />
                <PinpointMarker
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: 'KS_INVESTMENT',
                    ksInvestment: {
                      field: 'ks.investment.overview',
                      path: { project: id },
                    },
                  })}
                >
                  <Text variant="body" text={overview} weight="regular" />
                </PinpointMarker>
              </Space>,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper borderBottom={0} title={t('others')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <ProjectRemarkPinpointItem
                key="remark"
                projectId={id}
                pinpointSheetType="KS_INVESTMENT"
                remark={remark}
              />,
              <ProjectOldProjectNumberPinpointItem
                key="old-project-number"
                projectId={id}
                pinpointSheetType="KS_INVESTMENT"
                oldProjectNumber={oldProjectNumber}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>
    </>
  );
};
